import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Trophy, Crown, TrendingUp, Flame, 
  Zap, Dumbbell, Award, ChevronUp
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "workouts", label: "Workouts", icon: Dumbbell, color: "orange" },
  { id: "streak", label: "Streak", icon: Flame, color: "red" },
  { id: "vertical", label: "Vertical", icon: TrendingUp, color: "emerald" },
  { id: "badges", label: "Badges", icon: Award, color: "yellow" },
];

const PERIOD_OPTIONS = [
  { id: "weekly", label: "This Week" },
  { id: "monthly", label: "This Month" },
  { id: "all", label: "All Time" },
];

const Leaderboard = () => {
  const [category, setCategory] = useState("workouts");
  const [period, setPeriod] = useState("all");
  const [rankings, setRankings] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRankings();
  }, [category, period]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchMyStats();
  }, []);

  const fetchRankings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/leaderboard/rankings`, {
        params: { category, period, limit: 20 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRankings(response.data.rankings || []);
      setMyRank(response.data.my_rank);
    } catch (err) {
      console.error("Leaderboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/leaderboard/my-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyStats(response.data);
    } catch (err) { /* swallowed: non-critical */ }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Trophy className="w-5 h-5 text-zinc-300" />;
    if (rank === 3) return <Trophy className="w-5 h-5 text-amber-600" />;
    return <span className="text-sm font-heading text-white/30 w-5 text-center">{rank}</span>;
  };

  const getRankBg = (rank, isMe) => {
    if (isMe) return "bg-[#FF4500]/10 border-l-2 border-l-[#FF4500]";
    if (rank === 1) return "bg-yellow-500/5 border-l-2 border-l-yellow-400";
    if (rank === 2) return "bg-white/[0.02] border-l-2 border-l-white/30";
    if (rank === 3) return "bg-amber-600/5 border-l-2 border-l-amber-600";
    return "bg-transparent border-l-2 border-l-transparent";
  };

  const activeCat = CATEGORIES.find(c => c.id === category);

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6 lg:p-8 relative" data-testid="leaderboard-page">
      <div className="noise-overlay" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-2">Compete & Dominate</p>
            <h1 className="text-5xl md:text-7xl font-heading text-white uppercase tracking-tight leading-none">Leaderboard</h1>
            <p className="text-white/30 text-sm font-mono mt-2">
              {myStats ? `${myStats.total_athletes} athletes competing` : "Loading..."}
            </p>
          </div>
          <a
            href="/leaderboard/global"
            className="bg-amber-500 hover:bg-amber-400 text-black text-xs font-black uppercase tracking-wider px-4 py-2.5 inline-flex items-center gap-2 transition-colors"
            data-testid="open-global-leaderboard-btn"
          >
            <Trophy className="w-4 h-4" />
            Global Points →
          </a>
        </div>

        {/* My Rank Summary */}
        {myStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#FF4500]/20">
            <div className="bg-[#0A0A0A] p-5 text-center">
              <p className="text-3xl font-heading text-[#FF4500]">#{myStats.workout_rank}</p>
              <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em] mt-1">Workouts ({myStats.workout_count})</p>
            </div>
            <div className="bg-[#0A0A0A] p-5 text-center">
              <p className="text-3xl font-heading text-yellow-400">#{myStats.badge_rank}</p>
              <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em] mt-1">Badges ({myStats.badge_count})</p>
            </div>
            <div className="bg-[#0A0A0A] p-5 text-center">
              <p className="text-3xl font-heading text-white">{myStats.total_athletes}</p>
              <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em] mt-1">Total Athletes</p>
            </div>
            <div className="bg-[#0A0A0A] p-5 text-center">
              <p className="text-3xl font-heading text-[#CCFF00]">
                {myRank ? `#${myRank.rank}` : "-"}
              </p>
              <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em] mt-1">Current Category</p>
            </div>
          </div>
        )}

        {/* Category Tabs */}
        <Tabs value={category} onValueChange={setCategory}>
          <TabsList className="bg-[#0A0A0A] border border-white/[0.06] w-full rounded-none">
            {CATEGORIES.map((cat) => (
              <TabsTrigger
                key={cat.id}
                value={cat.id}
                data-testid={`leaderboard-tab-${cat.id}`}
                className="flex-1 rounded-none data-[state=active]:bg-[#FF4500] data-[state=active]:text-white font-heading uppercase tracking-wider text-xs"
              >
                <cat.icon className="w-4 h-4 mr-1.5" />
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Period Filter */}
        {category === "workouts" && (
          <div className="flex gap-px">
            {PERIOD_OPTIONS.map((p) => (
              <Button
                key={p.id}
                size="sm"
                className={`rounded-none font-heading uppercase tracking-wider text-xs ${
                  period === p.id 
                    ? "bg-[#FF4500] text-white hover:bg-[#FF6326]" 
                    : "bg-[#0A0A0A] border border-white/[0.06] text-white/40 hover:text-white hover:bg-white/[0.03]"
                }`}
                onClick={() => setPeriod(p.id)}
                data-testid={`period-${p.id}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
        )}

        {/* Rankings */}
        <div className="bg-[#0A0A0A] border border-white/[0.06]">
          <div className="p-4 border-b border-white/[0.06] flex items-center gap-2">
            {activeCat && <activeCat.icon className="w-4 h-4 text-[#FF4500]" />}
            <span className="text-sm font-heading text-white uppercase">{activeCat?.label} Rankings</span>
            <span className="text-[9px] font-mono text-white/30 ml-2">{rankings.length} athletes</span>
          </div>
          <div className="p-4">
            {loading ? (
              <div className="py-12 text-center text-white/30 font-mono">Loading rankings...</div>
            ) : rankings.length === 0 ? (
              <div className="py-12 text-center text-white/30 font-mono">No rankings yet. Start training!</div>
            ) : (
              <div className="space-y-2" data-testid="rankings-list">
                {rankings.map((entry) => (
                  <motion.div
                    key={entry.user_id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`flex items-center gap-3 p-3 rounded-sm border ${getRankBg(entry.rank, entry.is_me)}`}
                    data-testid={`ranking-${entry.rank}`}
                  >
                    {/* Rank */}
                    <div className="w-8 flex justify-center">{getRankIcon(entry.rank)}</div>

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-bold text-sm truncate ${entry.is_me ? "text-orange-400" : "text-white"}`}>
                        {entry.name}
                        {entry.is_me && <span className="text-[10px] text-orange-500 ml-2">(YOU)</span>}
                      </p>
                      <p className="text-[10px] text-zinc-500">{entry.detail}</p>
                    </div>

                    {/* Value */}
                    <div className="text-right">
                      <p className={`text-lg font-black ${
                        entry.rank === 1 ? "text-yellow-400" : entry.is_me ? "text-orange-400" : "text-white"
                      }`}>
                        {category === "vertical" ? `${entry.value}"` : entry.value}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* My Rank (if outside top list) */}
            {myRank && myRank.rank > rankings.length && (
              <div className="mt-4 pt-4 border-t border-white/[0.06] px-4 pb-4">
                <p className="text-[9px] font-mono text-white/30 mb-2 uppercase">Your position:</p>
                <div className="flex items-center gap-3 p-3 bg-[#FF4500]/10 border-l-2 border-l-[#FF4500]">
                  <div className="w-8 text-center">
                    <span className="text-sm font-heading text-[#FF4500]">#{myRank.rank}</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-sm text-[#FF4500]">{myRank.name} (YOU)</p>
                    <p className="text-[9px] font-mono text-white/30">{myRank.detail}</p>
                  </div>
                  <p className="text-lg font-heading text-[#FF4500]">{myRank.value}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Leaderboard;
